import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import ReCAPTCHA from "react-google-recaptcha";
import { EMPTY_CONTACT_DRAFT, useContactChat, type ContactFormDraft } from "../context/ContactChatContext";
import PillButton from "./PillButton";
import { SectionArrowRight } from "./SectionArrowDown";
import type { PillButtonVariant } from "./PillButton";

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
  captcha?: string;
}

const MINIMUM_FORM_FILL_TIME_MS = 2_000;

type Props = {
  className?: string;
  motionDelay?: number;
  /** Tighter spacing for chat-style popup */
  compact?: boolean;
  /** Embedded chat popup: single column, full-width submit, light text on dark */
  embeddedPopup?: boolean;
  submitVariant?: PillButtonVariant;
  /** Prefill inquiry fields without clearing contact details already entered. */
  inquiryPrefill?: {
    subject: string;
    message: string;
  } | null;
  pageContactLayout?: boolean;
  formInterlude?: ReactNode;
  submitLabel?: string;
};

export default function ContactForm({
  className = "",
  motionDelay = 0.2,
  compact = false,
  embeddedPopup = false,
  submitVariant,
  inquiryPrefill = null,
  pageContactLayout = false,
  formInterlude = null,
  submitLabel = "SEND MESSAGE",
}: Props) {
  const { contactDraft, setContactDraft, resetContactDraft } = useContactChat();

  const [localFormData, setLocalFormData] = useState<ContactFormDraft>(() => ({
    ...EMPTY_CONTACT_DRAFT,
  }));

  /** Popup: draft toàn app (giữ khi đổi route / đóng mở). Trang Contact: state riêng. */
  const formData = embeddedPopup ? contactDraft : localFormData;
  const setFormData = embeddedPopup ? setContactDraft : setLocalFormData;

  useEffect(() => {
    if (!inquiryPrefill || embeddedPopup) return;

    setLocalFormData((previous) => ({
      ...previous,
      subject: inquiryPrefill.subject,
      message: inquiryPrefill.message,
    }));
  }, [embeddedPopup, inquiryPrefill]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [website, setWebsite] = useState("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const formStartedAt = useRef(Date.now());

  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const contactApiUrl = import.meta.env.DEV
    ? "/contact.php"
    : import.meta.env.VITE_CONTACT_API_URL || "/contact.php";

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (!captchaToken) {
      newErrors.captcha = "Please confirm that you are not a robot";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setSubmitStatus("idle");
    setSubmitMessage("");

    if (Date.now() - formStartedAt.current < MINIMUM_FORM_FILL_TIME_MS) {
      setSubmitStatus("error");
      setSubmitMessage("Please wait a moment, then try again.");
      return;
    }

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const subject = formData.subject.trim() || "New contact form message";

      const response = await fetch(contactApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          job_title: formData.jobTitle.trim(),
          company_size: formData.companySize.trim(),
          company_website: formData.companyWebsite.trim(),
          subject,
          message: formData.message.trim(),
          website,
          recaptcha_token: captchaToken,
        }),
      });

      let data: { success?: boolean; message?: string } = {};
      try {
        data = (await response.json()) as { success?: boolean; message?: string };
      } catch {
        // A proxy or server error can return HTML instead of the API's JSON shape.
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Unable to send your message. Please try again.");
      }

      setSubmitStatus("success");
      setSubmitMessage(data.message || "Message sent successfully.");
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
      formStartedAt.current = Date.now();
      if (embeddedPopup) {
        resetContactDraft();
      } else {
        setLocalFormData({ ...EMPTY_CONTACT_DRAFT });
      }
    } catch (error) {
      console.error("Contact form submission failed", error);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
      setSubmitStatus("error");
      setSubmitMessage(
        error instanceof Error
          ? error.message
          : "Unable to send your message. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const gap = embeddedPopup ? "gap-x-4 gap-y-1.5 [@media_(max-height:700px)]:gap-y-1 md:gap-y-2.5 2xl:gap-x-5 2xl:gap-y-4" : compact ? "gap-x-4 gap-y-3" : "gap-x-8 gap-y-5";
  const field = embeddedPopup ? "relative space-y-1 pb-2 [@media_(max-height:700px)]:pb-1.5 md:space-y-1 md:pb-2.5 2xl:space-y-1.5 2xl:pb-3.5" : compact ? "relative space-y-1 pb-3" : "relative space-y-1.5 pb-3";
  const label = compact
    ? `${embeddedPopup ? "text-xs md:text-xs 2xl:text-lg" : "text-[10px] md:text-base"} whitespace-nowrap font-bold uppercase tracking-[0.12em] md:tracking-[0.14em] 2xl:tracking-[0.16em] ${embeddedPopup ? "text-gold/80" : "text-white/45"}`
    : "whitespace-nowrap text-xs font-bold uppercase tracking-widest text-white/40 lg:text-sm";
  const inputPad = embeddedPopup ? "py-1 text-sm leading-tight [@media_(max-height:700px)]:py-0.5 md:py-1.5 md:text-sm md:leading-tight 2xl:py-2.5 2xl:text-lg 2xl:leading-normal" : compact ? "py-2 text-sm" : "py-3 text-base lg:text-lg";
  const taRows = embeddedPopup ? 3 : compact ? 3 : 4;

  const gridCols = embeddedPopup ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2";
  /** Explicit light text on dark (avoids browser default dark text + bad autofill contrast). */
  const onDark =
    "text-white caret-brand placeholder:text-white/45 autofill:shadow-[inset_0_0_0_1000px_rgb(17_17_17)] autofill:text-white autofill:[-webkit-text-fill-color:white]";
  const fieldBorder = embeddedPopup ? "border-gold/20 focus:border-brand" : "border-white/10 focus:border-brand";
  const inputTheme = embeddedPopup
    ? "bg-black/15 px-0 focus:bg-brand/[0.03]"
    : "";
  const fullSpan = "md:col-span-2";
  const pairSpan = embeddedPopup ? "" : fullSpan;
  const submitButtonVariant = submitVariant ?? (embeddedPopup ? "gold" : "accent");
  const noGoldHover =
    "hover:border-brand/75! hover:bg-brand/10! hover:shadow-[0_0_28px_rgba(254,74,43,0.18)]! group-hover:[&_*]:text-gold!";
  const errorText = embeddedPopup
    ? "absolute bottom-0 left-0 min-h-2.5 text-[9px] font-semibold leading-none text-red-400 text-pretty md:min-h-2.5 md:text-[9px] 2xl:min-h-3 2xl:text-[11px]"
    : "absolute bottom-0 left-0 min-h-3 text-[11px] font-semibold leading-none text-red-400 text-pretty";

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: motionDelay }}
      className={`grid ${gridCols} ${gap} ${embeddedPopup ? "content-start pb-0 md:pb-0" : ""} ${className}`}
    >
      {pageContactLayout ? <h2 className="contact-form-section-title">Your contact details</h2> : null}
      <div className="pointer-events-none absolute -left-2500 h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={embeddedPopup ? "popup-website" : "contact-website"}>Website</label>
        <input
          id={embeddedPopup ? "popup-website" : "contact-website"}
          type="text"
          name="website"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label}>Name <span aria-hidden="true">*</span></label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          maxLength={100}
          required
          aria-required="true"
          className={`w-full min-w-0 border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${errors.name ? "border-red-500" : fieldBorder}`}
          placeholder="Your name *"
        />
        <p className={errorText}>{errors.name ?? ""}</p>
      </div>
      <div className={`min-w-0 ${pairSpan} ${field}`}>
        <label className={label}>Email address <span aria-hidden="true">*</span></label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          maxLength={254}
          required
          aria-required="true"
          className={`w-full min-w-0 border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${errors.email ? "border-red-500" : fieldBorder}`}
          placeholder="Your email *"
        />
        <p className={errorText}>{errors.email ?? ""}</p>
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label}>Phone number</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          maxLength={30}
          className={`w-full min-w-0 border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${fieldBorder}`}
          placeholder="Your phone number"
        />
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label}>Job title</label>
        <input
          type="text"
          name="jobTitle"
          value={formData.jobTitle}
          onChange={handleChange}
          maxLength={100}
          autoComplete="organization-title"
          className={`w-full min-w-0 border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${fieldBorder}`}
          placeholder="Your job title"
        />
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label}>Company size</label>
        <input
          type="text"
          name="companySize"
          value={formData.companySize}
          onChange={handleChange}
          maxLength={100}
          autoComplete="organization"
          className={`w-full min-w-0 border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${fieldBorder}`}
          placeholder="Company size"
        />
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label}>Company website</label>
        <input
          type="url"
          name="companyWebsite"
          value={formData.companyWebsite}
          onChange={handleChange}
          maxLength={2048}
          autoComplete="url"
          className={`w-full min-w-0 border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${fieldBorder}`}
          placeholder="Company website"
        />
      </div>

      <div className={`contact-form-subject min-w-0 ${pairSpan} ${field}`}>
        <label className={label}>Subject</label>
        <input
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          maxLength={160}
          className={`w-full min-w-0 border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${fieldBorder}`}
          placeholder="How can we help?"
        />
      </div>

      {formInterlude}

      {pageContactLayout ? <h2 className="contact-form-section-title">Tell us about your project</h2> : null}

      <div className={`min-w-0 ${fullSpan} ${field}`}>
        <label className={label}>Message <span aria-hidden="true">*</span></label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          maxLength={5000}
          rows={taRows}
          required
          aria-required="true"
          className={`w-full min-w-0 resize-none border-b bg-transparent outline-none transition-colors ${inputPad} ${onDark} ${inputTheme} ${errors.message ? "border-red-500" : fieldBorder}`}
          placeholder="Tell us about your project *"
        />
        <p className={errorText}>{errors.message ?? ""}</p>
      </div>

      <div className={`min-w-0 ${fullSpan}`}>
        {recaptchaSiteKey ? (
          <div className="contact-recaptcha max-w-full overflow-hidden">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              theme="dark"
              onChange={(token) => {
                setCaptchaToken(token);
                setErrors((previous) => ({ ...previous, captcha: undefined }));
              }}
              onExpired={() => {
                setCaptchaToken(null);
                setErrors((previous) => ({ ...previous, captcha: "Verification expired. Please try again" }));
              }}
              onErrored={() => {
                setCaptchaToken(null);
                setErrors((previous) => ({ ...previous, captcha: "Verification failed. Please try again" }));
              }}
            />
          </div>
        ) : (
          <p className="contact-verification-unavailable text-sm font-semibold text-red-400">Verification is temporarily unavailable.</p>
        )}
        <p className="mt-1 min-h-4 text-[11px] font-semibold text-red-400 text-pretty">{errors.captcha ?? ""}</p>
      </div>

      {embeddedPopup && submitStatus === "idle" ? null : (
        <p
          role="status"
          aria-live="polite"
          className={`${embeddedPopup ? "min-h-3 text-[10px] md:min-h-3 md:text-[10px] 2xl:min-h-5 2xl:text-sm" : "min-h-5 text-sm"} font-bold leading-snug ${
            submitStatus === "success" ? "text-green-500" : submitStatus === "error" ? "text-red-500" : "text-transparent"
          } text-pretty ${fullSpan}`}
        >
          {submitStatus === "success"
            ? submitMessage
            : submitStatus === "error"
              ? submitMessage
              : "."}
        </p>
      )}

      <div
        className={
          embeddedPopup
            ? `w-full ${fullSpan} md:w-full`
            : `flex justify-end md:col-span-2`
        }
      >
        <PillButton
          type="submit"
          disabled={isSubmitting || !recaptchaSiteKey}
          variant={submitButtonVariant}
          className={
            embeddedPopup
              ? `flex! min-h-9! w-auto max-w-[min(15rem,78vw)] justify-center border-gold/70 bg-gold/10 px-5 py-1.5 text-[11px] tracking-[0.18em] shadow-[0_0_24px_rgba(254,74,43,0.18)] [@media_(max-height:700px)]:py-1 md:min-h-9! md:w-full md:max-w-full md:px-5 md:py-1.5 md:text-xs md:tracking-[0.17em] 2xl:min-h-10! 2xl:px-7 2xl:py-2.5 2xl:text-[15px] 2xl:tracking-[0.2em] ${noGoldHover}`
              : compact
                ? `px-6 py-2 text-[12px] shadow-lg shadow-orange/20 ${submitButtonVariant === "gold" ? noGoldHover : ""}`
              : `px-8 py-2.5 text-[12px] shadow-xl shadow-orange/20 lg:text-sm ${submitButtonVariant === "gold" ? noGoldHover : ""}`
          }
          label={isSubmitting ? "SENDING..." : submitLabel}
          icon={
            isSubmitting ? undefined : <SectionArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          }
        />
      </div>
    </motion.form>
  );
}
